import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom, of, take, timeout } from 'rxjs';
import { ToastContainerComponent } from './core/components/toast-container.component';
import { ErrorFallbackComponent } from './core/components/error-fallback.component';
import { PwaInstallPromptComponent } from './core/components/pwa-install-prompt.component';
import { OfflineOverlayComponent } from './core/components/offline-overlay.component';
import { UnsupportedDeviceComponent } from './core/components/unsupported-device.component';
import { UnsupportedBrowserComponent } from './core/components/unsupported-browser.component';
import { InstallRequiredComponent } from './core/components/install-required.component';
import { UpdatePromptComponent } from './core/components/update-prompt.component';
import { ToastService } from './core/components/toast.service';
import { ConnectivityService } from './core/connectivity/connectivity.service';
import { MaintenanceComponent } from './core/components/maintenance.component';
import { LocalStorageManagerService } from './core/storage/local-storage-manager.service';
import { AuthStore } from './core/store/auth.store';
import { ThemeStore } from './core/store/theme.store';
import { ErrorBoundaryStore } from './core/error/error-boundary.store';
import { recoverFromChunkLoadError } from './core/error/chunk-load-error';
import { SingleInstanceService } from './core/services/single-instance.service';
import { AppUpdateService } from './core/services/app-update.service';
import { AppEventsService } from './core/services/app-events.service';
import { logAuthSession } from './core/auth/auth-session-log';
import { TokenRefreshService } from './core/network/token-refresh.service';
import { AppConfig } from './core/config/app-config';
import { AppConfigStore } from './core/app-config/app-config.store';
import { PwaInstallService } from './core/services/pwa-install.service';
import { isLauncherLaunch } from './core/utils/startup-helper';
import {
  isMobilePhone,
  isInstalledAppMode,
  isStandaloneDisplayMode,
  clearInstalledContext,
  whenInstalledDisplayMode,
  whenBrowserDisplayMode,
} from './core/analytics/device-context';
import {
  MOBILE_BROWSER_GATE_MAX_WAIT_MS,
  shouldBlockMobileBrowser,
} from './core/utils/mobile-browser-gate';
import {
  MAINTENANCE_GATE_MAX_WAIT_MS,
  isMaintenanceEnabled,
  resolveMaintenanceRetryLabel,
} from './core/utils/maintenance-gate';
import {
  shouldBlockBrowserAccess,
  PWA_INSTALLED_STORAGE_KEY,
} from './core/utils/browser-access-gate';
import { TelemetryLifecycleService } from './core/telemetry/telemetry-lifecycle.service';
import {
  BROWSER_VERSION_GATE_MAX_WAIT_MS,
  browserFamily,
  formatMinVersion,
  isRunOnlyChromiumShell,
  isUnsupportedBrowser,
  resolveEffectiveMinVersion,
  shouldBlockBrowserVersion,
  shouldBlockRunOnlyChromiumVersion,
} from './core/utils/browser-version-gate';
import { environment } from '../environments/environment';

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Root shell — Angular equivalent of Consumer's app/App.tsx (ErrorBoundary +
 * AppRoutes + EnvBadge + ToastContainer + PWAInstallPrompt) combined with the
 * `AuthHydrator` bootstrap effect from app/providers.tsx and the
 * SingleInstanceGuard wrapper.
 *
 * Access gates run first (maintenance → device → engine → version → install), then auth
 * hydration. Desktop gate order:
 *   0. CMS `maintenance_mode.isMaintenanceEnabled` → standalone Maintenance page (any device/session; fail-open).
 *   1. Phone UA → desktop-only page unless CMS `isMobileBrowserBlocked` is strictly false (fail-closed); tablets/desktop continue.
 *   2. Truly unsupported engines (IE, unknown non-Chromium) → unsupported screen.
 *   3. Run-only Chromium shells outside the env install allowlist (Opera, Vivaldi, …):
 *        - `blockBrowserAccess` true  → install-unsupported screen (open Chrome/Edge/Brave).
 *        - `blockBrowserAccess` false → version vs Chrome floor (CMS overrides env);
 *          if OK → login without PWA install prompt; if outdated → update screen.
 *   4. Whitelisted family (env `minBrowserVersions`: chrome/edge/firefox/safari/brave):
 *        - Version vs floor: CMS `minChromiumMajor` overrides env for chrome/edge/brave
 *          (raise or lower); env is the default. Firefox/Safari stay env-only.
 *        - Outdated → update screen.
 *        - Up to date + `blockBrowserAccess` true  → install/launch screen (unless standalone).
 *        - Up to date + `blockBrowserAccess` false → app with PWA install prompt allowed.
 *
 * After gates pass, startup matches Consumer step for step:
 *   5. Single-instance check (duplicate tabs get an "already open" screen).
 *   6. Hydrate encrypted storage, restore the in-memory access token.
 *   7. Session gate: if refresh exists and access is missing/expired (60s skew),
 *      silent refresh; auth rejection force-logs out (with returnUrl) before hydrate.
 *   8. Mark auth hydrated so route guards can evaluate; arm resume + refresh-expiry watch.
 *   9. Apply the theme class; listen for `auth:logout`, tab-visibility
 *      (session re-validate then data refresh), and roving arrow-key focus navigation.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    ToastContainerComponent,
    ErrorFallbackComponent,
    PwaInstallPromptComponent,
    OfflineOverlayComponent,
    UnsupportedDeviceComponent,
    UnsupportedBrowserComponent,
    InstallRequiredComponent,
    UpdatePromptComponent,
    MaintenanceComponent,
  ],
  template: `
    @if (mobileGatePending() || versionGatePending()) {
      <!-- Hold the gate spinner until device/engine/version compute finishes — avoids a flash of raw i18n keys. -->
      <div class="fixed inset-0 z-[60] flex min-h-dvh items-center justify-center bg-primary">
        <div
          class="h-10 w-10 animate-spin rounded-full border-4 border-accent border-t-transparent"
        ></div>
      </div>
    } @else if (isMaintenance()) {
      <app-maintenance [ctaLabel]="maintenanceCtaLabel()"></app-maintenance>
    } @else if (isMobileBlocked()) {
      <app-unsupported-device></app-unsupported-device>
    } @else if (isBrowserUnsupported()) {
      <app-unsupported-browser mode="unsupported"></app-unsupported-browser>
    } @else if (isInstallUnsupported()) {
      <app-unsupported-browser mode="installUnsupported"></app-unsupported-browser>
    } @else if (isBrowserOutdated()) {
      <app-unsupported-browser
        mode="outdated"
        [minVersion]="minBrowserVersion()"
      ></app-unsupported-browser>
    } @else if (isBrowserBlocked()) {
      <app-install-required></app-install-required>
    } @else if (singleInstance.isDuplicate() === true) {
      <div
        class="app-min-viewport flex flex-col items-center justify-center bg-primary px-6 text-center"
      >
        <div class="max-w-md rounded-2xl border border-border-overlay bg-surface p-10 shadow-xl">
          <div
            class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent"
          >
            <svg
              class="h-9 w-9 text-text-main"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z"
              />
            </svg>
          </div>
          <h2 class="mb-2 text-xl font-bold text-text-main">{{ appName }} is already open</h2>
          <p class="mb-8 text-sm leading-relaxed text-text-secondary">
            The app is running in another tab or window. Switch to that tab, or click below to use
            it here instead.
          </p>
          <button
            type="button"
            (click)="singleInstance.takeOver()"
            class="w-full rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-text-main transition-colors hover:bg-accent-hover"
          >
            Use Here Instead
          </button>
        </div>
      </div>
    } @else if (singleInstance.isDuplicate() === null) {
      <!-- Lock check in flight — render nothing to avoid a flash of the wrong UI. -->
    } @else if (errorBoundary.hasError()) {
      <app-error-fallback></app-error-fallback>
    } @else {
      <router-outlet></router-outlet>

      @if (isRouteLoading()) {
        <!--
          Angular equivalent of Consumer's Suspense boundary (fallback=LoadingFallback)
          around its whole route tree — same classes as Consumer's LoadingFallback,
          so the color/spinner is identical. Shown as a full-screen overlay (rather
          than swapping out router-outlet) so the outlet stays mounted for the
          router to activate into; see isRouteLoading's doc comment for the
          show-delay that keeps already-loaded/cached navigations flash-free.
        -->
        <div class="fixed inset-0 z-[60] flex min-h-dvh items-center justify-center bg-primary">
          <div
            class="h-10 w-10 animate-spin rounded-full border-4 border-accent border-t-transparent"
          ></div>
        </div>
      }

      <app-toast-container></app-toast-container>
      @if (showInstallPrompt()) {
        <app-pwa-install-prompt></app-pwa-install-prompt>
      }
      <app-update-prompt></app-update-prompt>
    }

    <!-- Must stay mounted while startup/browser gates and route activation are pending. -->
    <app-offline-overlay></app-offline-overlay>
  `,
})
export class App implements OnInit {
  /**
   * Delay before showing the route-loading overlay on any navigation *after*
   * the first one. Consumer's Suspense boundary has no such delay, but
   * React.lazy caches each chunk's import promise so an already-loaded route
   * never re-suspends — Angular's Router fires `NavigationStart` on every
   * navigation regardless of whether the target chunk (or guard evaluation)
   * is effectively instant. Without a grace period, fast/cached in-app
   * navigations would flash the spinner for no reason, which is exactly the
   * "unnecessary flash" this whole change is meant to remove. The very first
   * navigation is exempt (see `isRouteLoading`'s initial value below) so
   * cold-boot loading is still shown immediately, matching Consumer.
   */
  private static readonly ROUTE_LOADING_SHOW_DELAY_MS = 150;

  private localStorageManager = inject(LocalStorageManagerService);
  private appConfigStore = inject(AppConfigStore);
  private authStore = inject(AuthStore);
  private themeStore = inject(ThemeStore);
  private tokenRefresh = inject(TokenRefreshService);
  private appEvents = inject(AppEventsService);
  private appUpdate = inject(AppUpdateService);
  private pwaInstall = inject(PwaInstallService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private toast = inject(ToastService);
  private connectivity = inject(ConnectivityService);
  /** Tracks ngx-translate's in-flight `/i18n` load so the gate spinner can wait for real copy. */
  private readonly translateLoading$ = toObservable(this.translate.isLoading);

  singleInstance = inject(SingleInstanceService);
  errorBoundary = inject(ErrorBoundaryStore);
  private telemetryLifecycle = inject(TelemetryLifecycleService);
  readonly appName = AppConfig.JIO_CLOUD_PC_APP_NAME;

  /** CMS `maintenance_mode.isMaintenanceEnabled` is on — show the standalone Maintenance page. */
  readonly isMaintenance = signal(false);
  /**
   * CTA from CMS `showRetry`: `undefined` hides the button; empty string uses i18n;
   * non-empty string is the button label ("Go to home", "Try again", …).
   */
  readonly maintenanceCtaLabel = computed(() =>
    resolveMaintenanceRetryLabel(this.appConfigStore.configs()?.maintenance_mode),
  );
  /** CMS kill-switch is on and this is a phone browser — show the desktop-only page. */
  readonly isMobileBlocked = signal(false);
  /** Env kill-switch is on and this is a non-installed browser session — show the install-required page. */
  readonly isBrowserBlocked = signal(false);
  /** Engine cannot run the app at all (IE, unknown non-Chromium). */
  readonly isBrowserUnsupported = signal(false);
  /**
   * Run-only Chromium (Opera, …) while `blockBrowserAccess` is on — cannot install
   * from this browser; user must open Chrome/Edge/Brave.
   */
  readonly isInstallUnsupported = signal(false);
  /** Allowlisted / run-only browser older than the effective floor — show the update page. */
  readonly isBrowserOutdated = signal(false);
  /** Effective floor (CMS when set, else env), quoted on the outdated page. */
  readonly minBrowserVersion = signal(formatMinVersion());
  /**
   * Whether the dismissible PWA install dialog may render. False for run-only
   * Chromium when `blockBrowserAccess` is off (web login only, no install nag).
   */
  readonly showInstallPrompt = signal(true);
  /** Phone UA waiting on CMS before we know whether to block. */
  readonly mobileGatePending = signal(false);
  /**
   * True from first paint until the desktop engine/version gate finishes.
   * Starts true so Slow-3G / CMS waits show the spinner instead of raw i18n keys.
   */
  readonly versionGatePending = signal(true);

  /** True until the first navigation settles, then toggles per-navigation (see class doc above). */
  readonly isRouteLoading = signal(true);
  private routeLoadingShowTimer: ReturnType<typeof setTimeout> | null = null;

  /** Guards `continueBootstrap()` so it runs once, whether reached directly or after the display-mode settles. */
  private bootstrapContinued = false;
  /** Removes the post-install display-mode watcher (set only while the browser-access block is showing). */
  private installedModeWatcherCleanup?: () => void;
  /** Removes the "session resolved to a browser tab" watcher that re-applies the gate. */
  private browserModeWatcherCleanup?: () => void;

  constructor() {
    // Registered in the constructor (not as `private readonly x = effect(...)` fields) so the
    // effects aren't unused class members (TS6133); Angular keeps them alive for the component.

    // Re-evaluate maintenance whenever CMS configs update (cold start, Retry force-refresh,
    // stale refetch, foreground refresh). Entering maintenance tears down the journey shell;
    // leaving it reloads so bootstrap/session restore run cleanly.
    effect(() => {
      const enabled = isMaintenanceEnabled(this.appConfigStore.configs()?.maintenance_mode);
      if (enabled) {
        this.isMaintenance.set(true);
        this.versionGatePending.set(false);
        this.mobileGatePending.set(false);
        return;
      }
      // Only configs should re-trigger this effect — don't track isMaintenance writes.
      if (untracked(() => this.isMaintenance())) {
        window.location.reload();
      }
    });

    // CF-09: once per outage, explain why authenticated screens are failing while the session
    // can't be renewed for a transient reason (timeout, 5xx, CMS key) — the session is kept, so
    // without this the user only saw unexplained per-screen errors. Waits while the offline /
    // "Connection lost" overlay already explains things, while signed out, and until
    // translations are loaded, then shows once; a later outage shows it again.
    let degradedNoticeShown = false;
    effect(() => {
      if (!this.tokenRefresh.sessionDegraded()) {
        degradedNoticeShown = false;
        return;
      }
      if (degradedNoticeShown || this.connectivity.isOffline()) return;
      if (!this.authStore.isAuthenticated() || this.translate.isLoading()) return;
      degradedNoticeShown = true;
      untracked(() => this.toast.warning(this.translate.instant('common.sessionDegraded')));
    });
  }

  ngOnInit(): void {
    // Read before the router redirects `/` away and drops the query string.
    if (isLauncherLaunch(window.location.search)) this.localStorageManager.setLauncherSeenAt(Date.now());
    this.syncPwaDocumentTitle();
    this.appUpdate.init();
    // Start capturing `beforeinstallprompt` as early as possible so both the
    // dismissible prompt and the isBrowserBlocked hard-block screen can offer install.
    this.pwaInstall.init();
    this.trackRouteLoading();

    // Kick off the CMS config fetch as early as physically possible in the
    // app's lifecycle — well before the router/first route component has
    // even started downloading — so `appConfigReadyGuard` (see its own doc
    // comment) has the least possible amount of time left to wait once the
    // first route actually tries to activate. `ensureLoaded()` is de-duped,
    // so this doesn't cause a duplicate request when the guard also calls it.
    // Phone UAs also need this result for the `isMobileBrowserBlocked` kill-switch.
    // Maintenance gate and mid-session re-enable also depend on this fetch.
    void this.appConfigStore.ensureLoaded();

    // Foreground return may cross the 10-minute stale window — refresh CMS so
    // maintenance can turn on/off without requiring a full app restart.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') return;
      void this.appConfigStore.ensureLoaded();
    });

    this.themeStore.applyThemeClass(this.themeStore.mode());

    void this.bootstrap();
  }

  /**
   * Bootstrap gate order (maintenance → device → whitelist / run-only Chromium → version → install).
   * Mirrors the class-level access-flow doc; keep both in sync when the gate changes.
   *   0. CMS `maintenance_mode.isMaintenanceEnabled` → Maintenance page (any device; fail-open).
   *   1. Phone UA → desktop-only page unless CMS `isMobileBrowserBlocked` is strictly false (fail-closed); tablets/desktop continue.
   *   2. Truly unsupported engines (IE, unknown non-Chromium) → unsupported screen.
   *   3. Run-only Chromium (Opera, …):
   *        - `blockBrowserAccess` on  → install-unsupported screen.
   *        - `blockBrowserAccess` off → Chrome-floor version check (CMS overrides env);
   *          login without install prompt when up to date.
   *   4. Whitelisted family → version vs floors (CMS overrides env for chrome/edge/brave only).
   *   5. `blockBrowserAccess` → install-required unless already standalone.
   */
  private async bootstrap(): Promise<void> {
    // 0) Maintenance — applies to every device/session before other gates or auth hydrate.
    // Render gates use waitReady(…, true): instant when last launch's config was restored
    // (see AppConfigStore's constructor), else waits on the network up to its bound.
    await this.appConfigStore.waitReady(MAINTENANCE_GATE_MAX_WAIT_MS, true);
    if (isMaintenanceEnabled(this.appConfigStore.configs()?.maintenance_mode)) {
      this.isMaintenance.set(true);
      this.versionGatePending.set(false);
      this.mobileGatePending.set(false);
      return;
    }

    // 1) Device first — phones never hit the desktop version/install gates.
    if (isMobilePhone()) {
      this.versionGatePending.set(false);
      this.mobileGatePending.set(true);
      // Live answer only (no `true`): `isMobileBrowserBlocked` is a fail-closed kill-switch,
      // and gates run once — a cached "not blocked" would let phones through for a whole
      // session after CMS flips it on. Phones aren't the installed-app cold-start target.
      await this.appConfigStore.waitReady(MOBILE_BROWSER_GATE_MAX_WAIT_MS);
      if (shouldBlockMobileBrowser(this.appConfigStore.configs()?.isMobileBrowserBlocked)) {
        this.isMobileBlocked.set(true);
        this.mobileGatePending.set(false);
        return;
      }
      this.mobileGatePending.set(false);
      void this.continueBootstrap();
      return;
    }

    // Desktop gate: spinner already on (`versionGatePending` starts true).
    try {
      // 2) Truly unsupported (IE, unknown non-Chromium).
      if (isUnsupportedBrowser()) {
        this.isBrowserUnsupported.set(true);
        await this.waitForGateTranslations();
        return;
      }

      // 3) Run-only Chromium shells (Opera, Vivaldi, …) — not on the install allowlist.
      if (isRunOnlyChromiumShell()) {
        if (environment.blockBrowserAccess) {
          this.isInstallUnsupported.set(true);
          await this.waitForGateTranslations();
          return;
        }
        const cmsMin = await this.resolveCmsMinChromiumMajor();
        const effective = resolveEffectiveMinVersion('chrome', cmsMin);
        this.minBrowserVersion.set(String(effective));
        if (shouldBlockRunOnlyChromiumVersion(cmsMin)) {
          this.isBrowserOutdated.set(true);
          await this.waitForGateTranslations();
          return;
        }
        // Web login only — do not show the PWA install dialog.
        this.showInstallPrompt.set(false);
        void this.continueBootstrap();
        return;
      }

      // 4) Whitelisted family: version vs floor (CMS overrides env for Chromium families).
      const family = browserFamily();
      if (family !== null) {
        const cmsMin = await this.resolveCmsMinChromiumMajor();
        const effective = resolveEffectiveMinVersion(family, cmsMin);
        this.minBrowserVersion.set(String(effective));
        if (shouldBlockBrowserVersion(effective)) {
          this.isBrowserOutdated.set(true);
          await this.waitForGateTranslations();
          return;
        }
      }

      // Re-apply the browser gate if this session is — or resolves to — a plain
      // browser tab. A tab spawned from the PWA ("Open in Chrome") can briefly
      // mis-report an installed display mode at load, slip past the one-shot check
      // below, then settle to `display-mode: browser`. An installed PWA never
      // enters `browser` mode (a VDI session is `fullscreen`), so this can only
      // ever gate a genuine browser tab — never the real app.
      this.browserModeWatcherCleanup = whenBrowserDisplayMode(() => this.reapplyBrowserGate());

      this.destroyRef.onDestroy(() => {
        this.browserModeWatcherCleanup?.();
      });

      const installedApp = isInstalledAppMode();

      // Running as the installed app — persist it so later browser tabs (which
      // share localStorage on this origin) know the app is installed and can
      // offer "Open app" instead of re-prompting an install.
      if (installedApp) this.persistInstalledFlag();

      // 5) Build-time hard block for desktop/laptop/tablet browsers (whitelist only).
      if (shouldBlockBrowserAccess(environment.blockBrowserAccess, installedApp)) {
        this.isBrowserBlocked.set(true);
        this.armInstalledModeLift();
        return;
      }

      void this.continueBootstrap();
    } finally {
      this.versionGatePending.set(false);
    }
  }

  /**
   * Hold the gate spinner until the HTTP `/i18n` load settles (or times out).
   * Embedded fallback is already seeded in `app.config` for Slow 3G; waiting
   * here prefers the real bundle when it arrives before we paint the dead-end.
   */
  private async waitForGateTranslations(): Promise<void> {
    if (!this.translate.isLoading()) return;
    await firstValueFrom(
      this.translateLoading$.pipe(
        filter((loading) => !loading),
        take(1),
        timeout({ first: BROWSER_VERSION_GATE_MAX_WAIT_MS, with: () => of(false) }),
      ),
    );
  }

  /** Applies the desktop browser gate after a running PWA becomes a browser tab. */
  private reapplyBrowserGate(): void {
    if (isMobilePhone() || !environment.blockBrowserAccess) return;
    // Chrome can move the same browsing context rather than create a new tab.
    // Its sessionStorage marker therefore survives the move and must be removed
    // before the installed-mode recovery watcher evaluates the destination.
    clearInstalledContext();
    this.isBrowserBlocked.set(true);
    // If this tab already ran bootstrap (it mis-reported installed at load), it
    // may hold the single-instance lock. Release it so the real PWA launch is not
    // treated as a duplicate ("already open").
    this.singleInstance.release();
    // It also ran the session gate: disarm it, or its stale in-memory tokens could refresh on
    // focus with a rotated-away refresh token and log the real PWA window out (shared storage).
    this.tokenRefresh.suspendSessionLifecycle();
    // A window that BOOTED installed (so the sync gate below never armed a lift
    // watcher) can still be flipped to a browser at runtime. Arm the lift watcher
    // so returning to standalone clears the block rather than leaving the PWA on
    // the install screen until it is fully reopened.
    this.armInstalledModeLift();
  }

  /**
   * Best-effort CMS `minChromiumMajor` (overrides the env floor). Any cached config
   * (fresh or restored from the last launch) answers synchronously; otherwise wait up
   * to `BROWSER_VERSION_GATE_MAX_WAIT_MS` then proceed with env only.
   * Pending spinner is owned by {@link bootstrap}, not here.
   */
  private async resolveCmsMinChromiumMajor(): Promise<unknown> {
    await this.appConfigStore.waitReady(BROWSER_VERSION_GATE_MAX_WAIT_MS, true);
    return this.appConfigStore.configs()?.minChromiumMajor;
  }

  /**
   * Arms the watcher that lifts the browser-access block and restores the app
   * the instant the window returns to an installed display mode. Idempotent: a
   * second call while one is already armed is a no-op, so it is safe to call from
   * both the initial sync gate and the runtime `whenBrowserDisplayMode` re-gate.
   * After it fires it forgets the handle, so a later browser→installed cycle can
   * re-arm it — the block must be able to lift every time, not just once.
   *
   * Two ways to restore, decided by whether the app had already booted:
   *   - Never booted (blocked at startup, e.g. fresh install settling into
   *     standalone): run `continueBootstrap()`. The router's initial navigation
   *     is still pending and attaches to the router-outlet the moment the block
   *     is lifted, so the expected screen renders.
   *   - Already booted, then blocked at runtime (the window's content was
   *     reparented to `browser` via "Open in Chrome" and back via "Open in
   *     app"): the router-outlet was torn down while blocked, and re-creating it
   *     will NOT re-activate a route whose navigation already completed — it
   *     comes back blank. A reload is the reliable recovery: it re-runs the gate
   *     (now standalone → passes) and renders the expected screen. Safe from a
   *     single-instance "already open" false-positive because the browser tab
   *     released the lock when it entered browser mode (see the re-gate above).
   */
  private armInstalledModeLift(): void {
    if (this.installedModeWatcherCleanup) return;
    this.installedModeWatcherCleanup = whenInstalledDisplayMode(() => {
      this.installedModeWatcherCleanup?.();
      this.installedModeWatcherCleanup = undefined;
      this.persistInstalledFlag();
      if (this.bootstrapContinued) {
        window.location.reload();
        return;
      }
      this.isBrowserBlocked.set(false);
      void this.continueBootstrap();
    });
  }

  private persistInstalledFlag(): void {
    try {
      localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, '1');
    } catch {
      // Storage unavailable (private mode) — non-fatal; detection just falls back.
    }
  }

  /** The remainder of startup once device / browser / install gates have passed; runs at most once. */
  private async continueBootstrap(): Promise<void> {
    if (this.bootstrapContinued) return;
    this.bootstrapContinued = true;

    this.singleInstance.init();

    logAuthSession('bootstrap-start', 'info');
    this.authStore.markBootstrapStarted();

    this.tokenRefresh.initMultiTabLogout();

    this.localStorageManager
      .hydrate()
      .then(async () => {
        logAuthSession('storage-hydrated', 'debug');
        this.localStorageManager.restoreAccessTokenFromStorage();
        logAuthSession('access-token-restored', 'info');

        // Session gate before hydrate so a failed refresh cannot leave a stale JWT
        // as authenticated (AC3/AC6). Resume + idle expiry watch cover AC4/AC5.
        // Cached config is enough here: if validateSession() needs a refresh, refresh()
        // itself waits for the live config (and its API key) before calling the backend.
        await this.appConfigStore.waitReady(undefined, true);
        await this.tokenRefresh.validateSession('startup');
        this.tokenRefresh.initSessionLifecycle();
      })
      .catch((err: unknown) => {
        logAuthSession('bootstrap-failed', 'error', {
          error: err instanceof Error ? err.message : String(err),
        });
      })
      // Always (CF-03): every guard awaits `authStore.hydrate()`. If a step above throws and
      // hydrate is skipped, routing waits forever behind the loader.
      .finally(() => {
        this.authStore.hydrate();
        logAuthSession('hydrate-dispatched', 'info');
        this.telemetryLifecycle.initOnBoot();
      });

    window.addEventListener('auth:logout', () => this.authStore.logout());

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') return;
      // Session gate first (single-flighted with TokenRefreshService's own listener),
      // then data refresh only if the session survived.
      void this.tokenRefresh.validateSession('resume').then((result) => {
        if (result === 'logged_out' || result === 'anonymous' || result === 'suspended') return;
        if (!this.localStorageManager.getAccessToken()) return;
        logAuthSession('foreground-data-refresh', 'debug');
        this.appEvents.emitForegroundDataRefresh();
      });
    });

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

      const active = document.activeElement as HTMLElement | null;
      if (!active || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') return;

      const focusable = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      const idx = focusable.indexOf(active);
      if (idx === -1) return;

      const next =
        e.key === 'ArrowRight'
          ? focusable[(idx + 1) % focusable.length]
          : focusable[(idx - 1 + focusable.length) % focusable.length];

      next.focus();
      e.preventDefault();
    });
  }

  private trackRouteLoading(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        // First navigation: show immediately (isRouteLoading already starts
        // `true`). Subsequent navigations: only show if it's still in flight
        // after the grace period, so cached/instant transitions never flash.
        this.routeLoadingShowTimer = setTimeout(() => {
          this.isRouteLoading.set(true);
        }, App.ROUTE_LOADING_SHOW_DELAY_MS);
        return;
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        if (this.routeLoadingShowTimer) {
          clearTimeout(this.routeLoadingShowTimer);
          this.routeLoadingShowTimer = null;
        }
        this.isRouteLoading.set(false);
        this.syncPwaDocumentTitle();
      }

      // A failed lazy-chunk import during navigation (e.g. the very first
      // navigation right after "Update now"'s reload) surfaces as a
      // NavigationError, not through the zone/global ErrorHandler — the Router
      // cancels the navigation itself and never rethrows it. Left unhandled,
      // the router-outlet has nothing to activate: a blank screen instead of
      // GlobalErrorHandler's same reload/purge recovery. recoverFromChunkLoadError
      // no-ops for any other NavigationError (e.g. a guard rejection). See
      // chunk-load-error.ts.
      if (event instanceof NavigationError) {
        void recoverFromChunkLoadError(event.error);
      }
    });
  }

  /**
   * Keeps document.title empty in installed PWA mode to prevent Chromium
   * from appending duplicate title suffixes to the container bar, while
   * ensuring browser tabs always show the proper app title ("JioPC").
   */
  private syncPwaDocumentTitle(): void {
    if (typeof window === 'undefined') return;
    if (isInstalledAppMode() || isStandaloneDisplayMode()) {
      document.title = '';
    } else {
      document.title = 'JioPC';
    }
  }
}
